import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Camera, Lock, Mail, User, KeyRound } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', access_code: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error(t('auth.errors.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(t('auth.welcomeBack'));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (typeof msg === 'string' && msg.toLowerCase().includes('already')) {
        toast.error(t('auth.errors.emailTaken'));
      } else {
        toast.error(msg || t('auth.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
            <Camera className="w-8 h-8 text-white" />
          </Link>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
            PhotoPlan
          </h1>
          <p className="text-slate-600">{t('auth.createAccount')}</p>
        </div>

        <Card className="p-8 glass-card">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700 font-medium">
                {t('auth.fullName')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t('auth.namePlaceholder')}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="pl-11 h-12 bg-white/50 border-slate-200 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="pl-11 h-12 bg-white/50 border-slate-200 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.passwordPlaceholder')}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={8}
                  className="pl-11 h-12 bg-white/50 border-slate-200 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_code" className="text-slate-700 font-medium">
                {t('auth.accessCode')}
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="access_code"
                  type="text"
                  placeholder={t('auth.accessCodePlaceholder')}
                  value={form.access_code}
                  onChange={(e) => setForm({ ...form, access_code: e.target.value })}
                  className="pl-11 h-12 bg-white/50 border-slate-200 focus:border-emerald-500 uppercase"
                />
              </div>
              <p className="text-xs text-slate-500">{t('auth.accessCodeHint')}</p>
            </div>

            <p className="text-xs text-slate-500">{t('auth.termsAgree')}</p>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg shadow-lg transition-all"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.loading')}
                </div>
              ) : (
                t('auth.signUp')
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-slate-600">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-emerald-600 hover:underline font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
